// src\themes\choopar\components\BannerSlider.tsx
"use client";

import { useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay } from "swiper/modules";
import type { Swiper as SwiperClass } from "swiper";
import "swiper/css";
import "swiper/css/navigation";
import { SliderItemFrom } from "@/core/models/slider-model";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Image from "next/image";
import { Heading } from "@/components/ui/Heading";
import { cn } from "@/core/lib/utils";
import Link from "next/link";
import { loadThemeComponent } from "@/core/config/theme-component-loader";
const CustomButton = loadThemeComponent("CustomButton")

type SliderItemMediaProps = {
  url: string;
  alt?: string;
  className?: string;
  videoRef?: (el: HTMLVideoElement | null) => void; // NEW
};

const isVideoUrl = (url?: string) => {
  if (!url) return false;
  return /\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(url);
};

function SliderMedia({ url, alt, className, videoRef }: SliderItemMediaProps) {
  if (isVideoUrl(url)) {
    return (
      <video
        ref={videoRef} // NEW
        src={url}
        autoPlay
        muted
        loop
        playsInline
        className={cn(
          "absolute inset-0 w-full h-full object-cover z-0",
          className
        )}
      />
    );
  }

  // عکس دقیقاً مثل قبل، بدون هیچ تغییری
  return <Image src={url} alt={alt || ""} fill className={className} />;
}

type BannerSliderProps = {
  slides: SliderItemFrom[];
  varient?: "full" | "short";
  onlyDesktop?: boolean;
};

export default function BannerSlider({
  slides,
  varient = "full",
  onlyDesktop = false,
}: BannerSliderProps) {
  // NEW: نگه‌داشتن رفرنس ویدئوها بر اساس ایندکس اسلاید (ممکنه چند نسخه دسکتاپ/موبایل داشته باشیم)
  const videoRefs = useRef<Map<number, Set<HTMLVideoElement>>>(new Map());

  const registerVideoRef = (index: number) => (el: HTMLVideoElement | null) => {
    const map = videoRefs.current;
    let set = map.get(index);
    if (!set) {
      set = new Set();
      map.set(index, set);
    }
    if (el) set.add(el);
  };

  const playVideoForSlide = (index: number) => {
    const set = videoRefs.current.get(index);
    if (!set) return;
    set.forEach((video) => {
      // اگر مرورگر ویدئو رو موقع off-screen بودن pause کرده، دوباره از همون‌جا (یا از اول) پخشش کن
      if (video.paused) {
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch(() => {
            // اگر autoplay بلاک شد، بی‌صدا نادیده بگیر
          });
        }
      }
    });
  };

  // NEW: هر اسلایدی که مرورگر ممکنه pause کرده باشه رو موقع فعال شدن دوباره پخش کن
  const handleActiveSlideVideos = (swiper: SwiperClass) => {
    playVideoForSlide(swiper.realIndex);
  };

  return (
    <Swiper
      modules={[Navigation, Autoplay]}
      spaceBetween={30}
      slidesPerView={1}
      navigation={{
        nextEl: ".swiper-button-next-custom",
        prevEl: ".swiper-button-prev-custom",
      }}
      loop
      autoplay={{
        delay: 3000,
        disableOnInteraction: false,
      }}
      onSwiper={handleActiveSlideVideos} // NEW
      onSlideChange={handleActiveSlideVideos} // NEW
      className="w-full"
    >
      {slides?.map((slide, index) => {
        const isSlideVideo =
          isVideoUrl(slide.image_info?.url) ||
          isVideoUrl(slide.image_mobile_info?.url);
        const slideAutoplayDelay = isSlideVideo ? 15000 : 3000;

        const ImageElement = (() => {
          // حالت فقط دسکتاپ
          if (onlyDesktop) {
            return (
              slide.image_info?.url && (
                <SliderMedia
                  url={slide.image_info.url}
                  alt={slide.image_info.description || ""}
                  className="w-full h-svh object-cover z-0 absolute"
                  videoRef={registerVideoRef(index)} // NEW
                />
              )
            );
          }

          // حالت معمولی (دسکتاپ و موبایل جدا)
          return (
            <>
              <div className="hidden lg:flex">
                {slide.image_info?.url && (
                  <SliderMedia
                    url={slide.image_info.url}
                    alt={slide.image_info.description || ""}
                    className="w-full h-svh object-cover z-0 absolute"
                    videoRef={registerVideoRef(index)} // NEW
                  />
                )}
              </div>
              <div className="flex lg:hidden">
                {slide.image_mobile_info?.url && (
                  <SliderMedia
                    url={slide.image_mobile_info.url}
                    alt={slide.image_mobile_info.description || ""}
                    className="w-full h-svh object-cover z-0 absolute"
                    videoRef={registerVideoRef(index)} // NEW
                  />
                )}
              </div>
            </>
          );
        })();

        const Content = (
          <div
            className={cn(
              "relative flex justify-center items-center",
              varient === "full" ? "h-[calc(100vh-4.5rem)]" : "aspect-[16/9]"
            )}
          >
            {ImageElement}

            <div className="absolute inset-0 bg-black/10 z-0" />
            <div className="text-primary-100 absolute top-1/2 left-1/2 text-center -translate-y-1/2 -translate-x-1/2 z-10 space-y-3">
              <Heading
                level={1}
                className="text-primary-0 text-[calc(1.2rem+0.3vw)]"
              >
                {slide.title}
              </Heading>
              {slide.description && (
                <Heading level={2} className="text-primary-0">
                  {slide.description}
                </Heading>
              )}
              {slide.buttonContent && (
                <CustomButton size="lg" variant="black">
                  {slide.buttonContent}
                </CustomButton>
              )}
            </div>
          </div>
        );

        return (
          <SwiperSlide
            key={slide.priority}
            data-swiper-autoplay={slideAutoplayDelay}
          >
            {slide.link ? <Link href={slide.link}>{Content}</Link> : Content}
          </SwiperSlide>
        );
      })}

      <button className="swiper-button-prev-custom absolute ltr:left-4 top-1/2 -translate-y-1/2 z-10 text-primary-100 cursor-pointer disabled:opacity-0 disabled:cursor-default rtl:rotate-180 rtl:right-4">
        <ChevronLeft className="w-10 h-10 stroke-1 text-primary-0" />
      </button>
      <button className="swiper-button-next-custom absolute ltr:right-4 top-1/2 -translate-y-1/2 z-10 text-primary-100 cursor-pointer disabled:opacity-0 disabled:cursor-default rtl:rotate-180 rtl:left-4">
        <ChevronRight className="w-10 h-10 stroke-1 text-primary-0" />
      </button>
    </Swiper>
  );
}